import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import HomePage from "./pages/HomePage";
import AuditFormPage from "./pages/AuditFormPage";
import ReportPreviewPage from "./pages/ReportPreviewPage";
import FullReportPage from "./pages/FullReportPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import Discover from "./pages/Discover";
import AgencyPage from "./pages/AgencyPage";
import MyReportsPage from "./pages/MyReportsPage";
import ShopifyPage from "./pages/ShopifyPage";
import RestaurantPage from "./pages/RestaurantPage";
import HealthcarePage from "./pages/HealthcarePage";
import RealEstatePage from "./pages/RealEstatePage";
import PropertyManagementPage from "./pages/PropertyManagementPage";
import RetailPage from "./pages/RetailPage";
import EcommercePage from "./pages/EcommercePage";
import HVACPage from "./pages/HVACPage";
import HomeServicesPage from "./pages/HomeServicesPage";
import LegalPage from "./pages/LegalPage";
import DentalPage from "./pages/DentalPage";
import SaaSPage from "./pages/SaaSPage";
import ConstructionPage from "./pages/ConstructionPage";
import AccountingPage from "./pages/AccountingPage";
import AutoRepairPage from "./pages/AutoRepairPage";
import MedSpaPage from "./pages/MedSpaPage";
import UpgradePage from "./pages/UpgradePage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import MarketingPage from "./pages/MarketingPage";
import LeadMagnetPage from "./pages/LeadMagnetPage";
import SignalInboxPage from "./pages/SignalInboxPage";
import SignalInboxSuccessPage from "./pages/SignalInboxSuccessPage";
import OutcomeCapturePage from "./pages/OutcomeCapturePage";
import DiscoverIndustryPage from "./pages/DiscoverIndustryPage";
import NotFoundPage from "./pages/NotFoundPage";
import ScrollToTop from "./components/ScrollToTop";
import LanguageSuggestionBanner from "./components/LanguageSuggestionBanner";
import ExitIntentModal from "./components/ExitIntentModal";
import { Toaster } from "./components/ui/sonner";

import { getTranslation, tWithVars } from "./i18n/translations";

// Global language context
export const LanguageContext = React.createContext({
  lang: "en",
  t: (key) => key,
  setLang: () => {},
});

// Wrapper that forces a specific language on mount
const LanguageRoute = ({ lang: forcedLang, children }) => {
  const { setLang } = React.useContext(LanguageContext);
  useEffect(() => {
    setLang(forcedLang);
  }, [forcedLang, setLang]);
  return children;
};

// ─── Cookie Consent Banner ──────────────────────────────────────────────────
function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("aiw_cookie_consent");
    if (!consent) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem("aiw_cookie_consent", "accepted");
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem("aiw_cookie_consent", "declined");
    // Disable GA if user declines
    window["ga-disable-G-FQX15TJ979"] = true;
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-t border-white/10 px-4 py-4 shadow-2xl">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-sm text-slate-300 text-center sm:text-left">
          We use cookies for analytics and to improve your experience.{" "}
          <a href="/privacy" className="text-[#3b82f6] hover:underline">Privacy Policy</a>
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={decline}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors">
            Decline
          </button>
          <button onClick={accept}
            className="px-4 py-2 text-sm font-semibold text-white bg-[#3b82f6] hover:bg-blue-600 rounded-lg transition-colors">
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [lang, setLang] = useState("en");

  // Load saved language
  useEffect(() => {
    const stored = localStorage.getItem("aiw_lang");
    if (stored === "en" || stored === "es" || stored === "tr") {
      setLang(stored);
    }
  }, []);

  // Persist language
  useEffect(() => {
    localStorage.setItem("aiw_lang", lang);
  }, [lang]);

  const t = (key, vars) => vars ? tWithVars(lang, key, vars) : getTranslation(lang, key);

  return (
    <LanguageContext.Provider value={{ lang, t, setLang }}>
      <div className="App app-shell">
        <BrowserRouter>
          <ScrollToTop />
          <LanguageSuggestionBanner />
          <ExitIntentModal />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/audit" element={<AuditFormPage />} />
            <Route
              path="/report-preview/:reportId"
              element={<ReportPreviewPage />}
            />
            <Route
              path="/full-report/:reportId"
              element={<FullReportPage />}
            />
            <Route
              path="/payment/:reportId"
              element={<PaymentPage />}
            />
            <Route
              path="/payment-success"
              element={<PaymentSuccessPage />}
            />
            <Route
              path="/outcomes/:reportId/:token"
              element={<OutcomeCapturePage />}
            />
            {/* Per-industry SEO pages — matched BEFORE the catch-all /discover
                so React Router lands on the right component. Each URL is a
                distinct, indexable page registered in sitemap.xml. */}
            <Route path="/discover/:industry" element={<DiscoverIndustryPage />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/agency" element={<AgencyPage />} />
            <Route path="/my-reports" element={<MyReportsPage />} />
            <Route path="/shopify" element={<ShopifyPage />} />
            <Route path="/restaurant" element={<RestaurantPage />} />
            <Route path="/healthcare" element={<HealthcarePage />} />
            <Route path="/real-estate" element={<RealEstatePage />} />
            <Route path="/property-management" element={<PropertyManagementPage />} />
            <Route path="/pm" element={<PropertyManagementPage />} />
            <Route path="/landlord" element={<PropertyManagementPage />} />
            <Route path="/landlords" element={<PropertyManagementPage />} />
            <Route path="/retail" element={<RetailPage />} />
            <Route path="/ecommerce" element={<EcommercePage />} />
            <Route path="/e-commerce" element={<EcommercePage />} />
            <Route path="/hvac" element={<HVACPage />} />
            {/* Home services umbrella + niche-specific entry routes. All five
                paths render the same HomeServicesPage which detects the
                pathname via useLocation() and swaps niche-specific hero copy.
                One landing page, five Google Ads targets. */}
            <Route path="/home-services" element={<HomeServicesPage />} />
            <Route path="/plumbing" element={<HomeServicesPage />} />
            <Route path="/electrical" element={<HomeServicesPage />} />
            <Route path="/roofing" element={<HomeServicesPage />} />
            <Route path="/pool" element={<HomeServicesPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/dental" element={<DentalPage />} />
            <Route path="/saas" element={<SaaSPage />} />
            <Route path="/construction" element={<ConstructionPage />} />
            <Route path="/accounting" element={<AccountingPage />} />
            <Route path="/auto-repair" element={<AutoRepairPage />} />
            <Route path="/med-spa" element={<MedSpaPage />} />
            <Route path="/medspa" element={<MedSpaPage />} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/marketing" element={<MarketingPage />} />
            <Route path="/signal-inbox" element={<SignalInboxPage />} />
            <Route path="/signal-inbox/success" element={<SignalInboxSuccessPage />} />
            <Route path="/plan/:leadId" element={<LeadMagnetPage />} />

            {/* Spanish routes */}
            <Route path="/es" element={<LanguageRoute lang="es"><HomePage /></LanguageRoute>} />
            <Route path="/es/audit" element={<LanguageRoute lang="es"><AuditFormPage /></LanguageRoute>} />
            <Route path="/es/dental" element={<LanguageRoute lang="es"><DentalPage /></LanguageRoute>} />
            <Route path="/es/restaurant" element={<LanguageRoute lang="es"><RestaurantPage /></LanguageRoute>} />
            <Route path="/es/hvac" element={<LanguageRoute lang="es"><HVACPage /></LanguageRoute>} />
            <Route path="/es/healthcare" element={<LanguageRoute lang="es"><HealthcarePage /></LanguageRoute>} />
            <Route path="/es/legal" element={<LanguageRoute lang="es"><LegalPage /></LanguageRoute>} />
            <Route path="/es/real-estate" element={<LanguageRoute lang="es"><RealEstatePage /></LanguageRoute>} />
            <Route path="/es/retail" element={<LanguageRoute lang="es"><RetailPage /></LanguageRoute>} />
            <Route path="/es/ecommerce" element={<LanguageRoute lang="es"><EcommercePage /></LanguageRoute>} />
            <Route path="/es/shopify" element={<LanguageRoute lang="es"><ShopifyPage /></LanguageRoute>} />
            <Route path="/es/construction" element={<LanguageRoute lang="es"><ConstructionPage /></LanguageRoute>} />
            <Route path="/es/accounting" element={<LanguageRoute lang="es"><AccountingPage /></LanguageRoute>} />
            <Route path="/es/auto-repair" element={<LanguageRoute lang="es"><AutoRepairPage /></LanguageRoute>} />
            <Route path="/es/med-spa" element={<LanguageRoute lang="es"><MedSpaPage /></LanguageRoute>} />
            <Route path="/es/saas" element={<LanguageRoute lang="es"><SaaSPage /></LanguageRoute>} />
            <Route path="/es/discover" element={<LanguageRoute lang="es"><Discover /></LanguageRoute>} />

            {/* Turkish routes */}
            <Route path="/tr" element={<LanguageRoute lang="tr"><HomePage /></LanguageRoute>} />
            <Route path="/tr/audit" element={<LanguageRoute lang="tr"><AuditFormPage /></LanguageRoute>} />
            <Route path="/tr/dental" element={<LanguageRoute lang="tr"><DentalPage /></LanguageRoute>} />
            <Route path="/tr/restaurant" element={<LanguageRoute lang="tr"><RestaurantPage /></LanguageRoute>} />
            <Route path="/tr/hvac" element={<LanguageRoute lang="tr"><HVACPage /></LanguageRoute>} />
            <Route path="/tr/healthcare" element={<LanguageRoute lang="tr"><HealthcarePage /></LanguageRoute>} />
            <Route path="/tr/legal" element={<LanguageRoute lang="tr"><LegalPage /></LanguageRoute>} />
            <Route path="/tr/real-estate" element={<LanguageRoute lang="tr"><RealEstatePage /></LanguageRoute>} />
            <Route path="/tr/retail" element={<LanguageRoute lang="tr"><RetailPage /></LanguageRoute>} />
            <Route path="/tr/ecommerce" element={<LanguageRoute lang="tr"><EcommercePage /></LanguageRoute>} />
            <Route path="/tr/shopify" element={<LanguageRoute lang="tr"><ShopifyPage /></LanguageRoute>} />
            <Route path="/tr/construction" element={<LanguageRoute lang="tr"><ConstructionPage /></LanguageRoute>} />
            <Route path="/tr/accounting" element={<LanguageRoute lang="tr"><AccountingPage /></LanguageRoute>} />
            <Route path="/tr/auto-repair" element={<LanguageRoute lang="tr"><AutoRepairPage /></LanguageRoute>} />
            <Route path="/tr/med-spa" element={<LanguageRoute lang="tr"><MedSpaPage /></LanguageRoute>} />
            <Route path="/tr/saas" element={<LanguageRoute lang="tr"><SaaSPage /></LanguageRoute>} />
            <Route path="/tr/discover" element={<LanguageRoute lang="tr"><Discover /></LanguageRoute>} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <CookieConsent />
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
